import {
  Button,
  Field,
  Input,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkFilled,
  ErrorCircleFilled,
  InfoFilled,
  QuestionCircleFilled,
  WarningFilled,
} from '@fluentui/react-icons';
import { useDialogService, useT } from '@powerportalspro/react';
import { Section, SectionColumn } from '@powerportalspro/react-fluent';
import { useState } from 'react';

// Same localization keys the Blazor `DialogServiceMainDemo` resolves to,
// so both stacks pull from the same JSON. Framework flattens the keys
// to `components.<FullTypeName>.<key>`.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Utilities.DialogService.DialogServiceMainDemo';

const useStyles = makeStyles({
  // Header sits on a SectionColumn `<Header>` slot — give the title +
  // message inputs a horizontal row that wraps on narrow widths.
  inputs: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalL,
    alignItems: 'flex-start',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    alignItems: 'flex-start',
  },
});

export function DialogServiceMainSample() {
  const styles = useStyles();
  const t = useT();
  const dialog = useDialogService();
  const [title, setTitle] = useState('Dialog Title');
  const [message, setMessage] = useState('Dialog message');

  const showConfirmation = async () => {
    const result = await dialog.showConfirmationAsync(message, {
      title,
      primaryText: t(`${STRINGS_BASE}.yes`),
      secondaryText: t(`${STRINGS_BASE}.no`),
    });
    await dialog.showInfoAsync(
      t(`${STRINGS_BASE}.confirmation-result-message`, [String(!result.cancelled)]),
      {
        title: t(`${STRINGS_BASE}.confirmation-result-title`),
        primaryText: t(`${STRINGS_BASE}.ok`),
      },
    );
  };

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.inputs}>
            <Field label={t(`${STRINGS_BASE}.dialog-title-label`)}>
              <Input value={title} onChange={(_, d) => setTitle(d.value)} />
            </Field>
            <Field label={t(`${STRINGS_BASE}.dialog-message-label`)}>
              <Textarea
                value={message}
                onChange={(_, d) => setMessage(d.value)}
                rows={2}
              />
            </Field>
          </div>
        }
      >
        <div className={styles.buttons}>
          <Button
            icon={<QuestionCircleFilled style={{ color: tokens.colorBrandForeground1 }} />}
            onClick={showConfirmation}
          >
            {t(`${STRINGS_BASE}.show-confirmation-dialog`)}
          </Button>
          <Button
            icon={<ErrorCircleFilled style={{ color: tokens.colorPaletteRedForeground1 }} />}
            onClick={() => dialog.showErrorAsync(message, { title, primaryText: t(`${STRINGS_BASE}.ok`) })}
          >
            {t(`${STRINGS_BASE}.show-error-dialog`)}
          </Button>
          <Button
            icon={<InfoFilled style={{ color: tokens.colorBrandForeground1 }} />}
            onClick={() => dialog.showInfoAsync(message, { title, primaryText: t(`${STRINGS_BASE}.ok`) })}
          >
            {t(`${STRINGS_BASE}.show-info-dialog`)}
          </Button>
          <Button
            icon={<CheckmarkFilled style={{ color: tokens.colorPaletteGreenForeground1 }} />}
            onClick={() => dialog.showSuccessAsync(message, { title, primaryText: t(`${STRINGS_BASE}.ok`) })}
          >
            {t(`${STRINGS_BASE}.show-success-dialog`)}
          </Button>
          <Button
            icon={<WarningFilled style={{ color: tokens.colorStatusWarningForeground1 }} />}
            onClick={() => dialog.showWarningAsync(message, { title, primaryText: t(`${STRINGS_BASE}.ok`) })}
          >
            {t(`${STRINGS_BASE}.show-warning-dialog`)}
          </Button>
        </div>
      </SectionColumn>
    </Section>
  );
}
